<script>
import { mapState, mapActions } from 'vuex'
import ApiKeyMixin from '@lib/mixins/api-key'
import FormMixin from '@lib/mixins/modal-form'
import scopes from './scopes'

export default {
  name: 'ApiKeyDetails',
  mixins: [
    ApiKeyMixin,
    FormMixin,
  ],
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      progress: false,
      form: {
        name: '',
        roles: [],
        active: true,
      },
      selectedScopes: [],
      selectedRoles: [],
      rules: {
        name: [
          {
            required: true,
          },
        ],
        // TODO Forbid empty scopes
        roles: [],
      },
    }
  },
  computed: {
    ...mapState('apiKeys', ['apiKey']),
    ...mapState('me', [
      'provider',
    ]),
    scopes() {
      return scopes
    },
    isEdit() {
      return this.$route.meta.edit
    },
    title() {
      return (this.isEdit ? 'Edit' : 'Create') + ' API Key'
    },
    backRoute() {
      return { name: this.isEdit ? 'api-key' : 'api-keys' }
    },
  },
  async created() {
    if (this.isEdit) {
      this.loading = true

      const [error] = await this.showApiKey({ id: this.apiKey.id })

      if (error) {
        this.$router.replace(this.backRoute)
      }

      this.form = this.apiKey
      this.selectedScopes = this.preparedScopes.map(i => i.group)
      this.selectedRoles = this.scopes
        .map(i => {
          if (this.selectedScopes.includes(i.group)) {
            return this.preparedScopes.find(ps => ps.group === i.group).scopes
          } else return []
        })
      this.loading = false
    }

    this.$nextTick(() => {
      this.cloneOriginalData()
    })
  },
  methods: {
    ...mapActions('apiKeys', [
      'showApiKey',
      'createApiKey',
      'updateApiKey',
    ]),
    checkDisabled(group) {
      return !this.selectedScopes.includes(group)
    },
    flattenScopes() {
      const preparedScopes = []
      const { scopes, selectedScopes, selectedRoles } = this
      selectedScopes.forEach(s => {
        const index = scopes.findIndex(e => s === e.group)
        selectedRoles[index].forEach(r => preparedScopes.push(`${s}:${r}`))
      })
      return preparedScopes
    },
    async onSubmit() {
      const formValid = await this.validate()

      if (!formValid) {
        return false
      }

      this.progress = true
      const { form, provider, apiKey } = this
      form.roles = this.flattenScopes()
      form.provider = `/providers/${provider.id}`

      let response
      if (this.isEdit) {
        response = await this.updateApiKey({ id: apiKey.id, form })
      } else {
        response = await this.createApiKey({ form })
      }

      const [error, success] = response

      if (error && error.violations) {
        error.violations = Object.keys(error.violations).reduce((acc, key) => {
          const index = key.split('.')[1]
          const fieldName = form.roles[index]
          acc[fieldName] = ['The selected rule is invalid.']
          return acc
        }, {})
      }

      this.$notifier({
        response,
        successMsg: `API Key ${this.form.name} successfully ${this.edit ? 'edited' : 'created'}`,
      })

      if (!error) {
        this.$router.push({
          name: 'api-key',
          params: {
            id: success.id,
            providerId: success.providerId,
            allow: true,
          },
        })
      }
      this.progress = false
    },
    validate() {
      return this.$refs.form.validate().catch(() => false)
    },
  },
  beforeRouteLeave(to, from, next) {
    if (to.params.allow) {
      next()
    } else {
      if (this.checkIfFormChanged()) {
        this.$modal.show('form-leave-confirm', {
          on: {
            leave: next,
          },
        })
      } else {
        next()
      }
    }
  },
}
</script>

<template>
  <main-layout
    :title="title"
    :loading="loading"
    :back="backRoute"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
      :disabled="progress"
    >
      <el-form-item
        label="Provider Name"
        :class="$style.wrapper"
      >
        <el-input
          disabled
          :value="provider.name"
        />
      </el-form-item>

      <el-form-item
        label="API Key Name"
        prop="name"
        :class="$style.wrapper"
      >
        <el-input
          v-model="form.name"
        />
      </el-form-item>

      <el-form-item
        label="Scopes"
        prop="roles"
        :class="$style.scopes"
      >
        <div
          v-for="(scope, index) in scopes"
          :key="index"
          :class="$style.scope"
        >
          <el-checkbox
            v-model="selectedScopes"
            :label="scope.group"
            :class="$style.scopeCheckbox"
          />

          <el-select
            v-model="selectedRoles[index]"
            placeholder="Select scopes"
            multiple
            :disabled="checkDisabled(scope.group)"
            :class="$style.scopeSelect"
          >
            <el-option
              v-for="(s, i) in scope.scopes"
              :key="i"
              :value="s"
              :label="s"
            />
          </el-select>
        </div>
      </el-form-item>

      <el-form-item
        prop="active"
        data-test="switch"
      >
        <el-switch
          v-model="form.active"
        />
        <span
          :class="[
            $style.switchLabel,
            form.active && $style.switchLabelActive,
          ]"
        >
          {{ form.active ? 'Active' : 'Inactive' }}
        </span>
      </el-form-item>

      <div>
        <el-button
          type="outline"
          class="el-button--wide"
          @click.stop="$router.push(backRoute)"
        >
          Cancel
        </el-button>

        <el-button
          type="primary"
          :loading="progress"
          class="el-button--wide"
          @click.stop="onSubmit"
        >
          Save
        </el-button>
      </div>
    </el-form>
  </main-layout>
</template>

<style lang="scss" module>
.wrapper {
  width: rem(640px);
}

.scopes {
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: rem(640px);

  :global {

    .el-form-item__content {
      flex-direction: column;
    }

    .el-form-item__error {
      font-size: rem(14px);
    }

    .el-checkbox {
      align-items: center;
      height: rem(40px);
    }

    .el-form-item__label {
      padding-bottom: rem(6px) !important;
    }
  }

  &:global(.is-error > .el-form-item__content) {
    margin-bottom: rem(24px);
  }
}

.scope {
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: rem(0 0 20px);
}

.scopeCheckbox {
  width: rem(200px);
  min-width: rem(200px);
}

.scopeSelect {
  display: flex;
  align-items: center;
  margin-left: 1rem;
}

.switchLabel {
  margin-left: rem(14px);
  transition: color 0.3s;
}

.switchLabelActive {
  color: var(--color-success);
}
</style>
